import React, { useState, useEffect } from 'react';
import AppSidebar from '@/components/AppSidebar';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, CheckCircle, Clock, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TasksPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    asset_id: '',
    title: '',
    description: '',
    assigned_to: 'Maintenance Team'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [tasksRes, assetsRes] = await Promise.all([
        axios.get(`${API}/tasks`, config),
        axios.get(`${API}/assets`, config)
      ]);
      setTasks(tasksRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/tasks`, formData, config);
      toast.success('Task created successfully');
      setShowCreateModal(false);
      setFormData({ asset_id: '', title: '', description: '', assigned_to: 'Maintenance Team' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const completeTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/tasks/${taskId}/complete`, {}, config);
      toast.success('Task completed');
      loadData();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="tasks-page">
      <AppSidebar />
      <div className="flex-1 min-w-0 overflow-y-auto h-screen p-8">
      <div className="max-w-5xl mx-auto">
        <Button
          variant="ghost"
          className="text-gray-600 hover:text-black mb-6"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
            O&M Tasks
          </h1>
          {JSON.parse(localStorage.getItem('user') || '{}')?.role !== 'guest' && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white"
              data-testid="add-task-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          )}
        </div>

        {tasks.length === 0 ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No pending tasks</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="tasks-list">
            {tasks.map((task) => (
              <Card key={task.id} className="bg-white border-gray-200 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Clock className="w-5 h-5 text-red-500" />
                        <h3 className="text-black font-semibold text-lg">{task.title}</h3>
                        <Badge>{task.status}</Badge>
                      </div>
                      <p className="text-gray-700 mb-3">{task.description}</p>
                      <p className="text-sm text-gray-600">
                        Created: {new Date(task.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="ml-4">
                      {task.status !== 'Completed' && JSON.parse(localStorage.getItem('user') || '{}')?.role !== 'guest' && (
                        <Button
                          size="sm"
                          onClick={() => completeTask(task.id)}
                          className="bg-red-500 hover:bg-red-600"
                          data-testid={`complete-task-${task.id}`}
                        >
                          Complete
                        </Button>
                      )}
                      {task.status !== 'Completed' && JSON.parse(localStorage.getItem('user') || '{}')?.role === 'guest' && (
                        <Badge variant="outline" className="text-gray-500">View Only</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Task Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-black">Create New Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="task-asset" className="text-gray-700">Asset</Label>
              <Select value={formData.asset_id} onValueChange={(value) => setFormData({...formData, asset_id: value})} required>
                <SelectTrigger className="bg-white border-gray-300 text-black" data-testid="task-asset-select">
                  <SelectValue placeholder="Select asset" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {assets.map(asset => (
                    <SelectItem key={asset.id} value={asset.id}>{asset.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-title" className="text-gray-700">Task Title</Label>
              <Input
                id="task-title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Cell Voltage Balancing"
                required
                className="bg-white border-gray-300 text-black"
                data-testid="task-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-description" className="text-gray-700">Description</Label>
              <Textarea
                id="task-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the task details..."
                required
                rows={4}
                className="bg-white border-gray-300 text-black"
                data-testid="task-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-assigned" className="text-gray-700">Assigned To</Label>
              <Input
                id="task-assigned"
                value={formData.assigned_to}
                onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                placeholder="Maintenance Team"
                className="bg-white border-gray-300 text-black"
                data-testid="task-assigned-input"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-gray-300 text-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white"
                data-testid="task-submit-btn"
              >
                Create Task
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default TasksPage;
